import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import RestaurantMenuCategoryService from '../../services/restaurant/RestaurantMenuCategoryService';
import RestaurantMenuItemService from '../../services/restaurant/RestaurantMenuItemService';
import RestaurantPreparationStationService from '../../services/restaurant/RestaurantPreparationStationService';

import { AppDispatch, RootState } from '../../store/store';

import {
  closeMenuCategory,
  closeMenuItem,
  openMenuCategoryCreate,
  openMenuCategoryEdit,
  openMenuItemCreate,
  openMenuItemEdit,
  resetMenuCategoryResults,
  setMenuCategoryPage,
  setMenuCategories,
  setMenuError,
  setMenuItemPage,
  setMenuLoading,
  setMenuProducts,
  setMenuStations,
  setMenuSaving,
  setMenuUpdating,
  updateMenuCategoryField,
  updateMenuItemField,
} from '../../store/slices/restaurant/restaurantMenuSlice';

import {
  RestaurantMenuCategoryDTO,
  RestaurantMenuCategoryFilters,
  RestaurantMenuCategoryForm,
  RestaurantMenuItemDTO,
  RestaurantMenuItemForm,
  RestaurantMenuItemFilters,
} from '../../types/restaurant/RestaurantMenuTypes';
import { RestaurantPageRequest } from '../../types/restaurant/RestaurantRecipeTypes';

import useAlerts from '../useAlerts';
import useApiErrorHandler from '../useApiErrorHandler';
import useConfirm from '../useConfirmModal';

const useRestaurantMenu = (branchId: number | '') => {
  const dispatch = useDispatch<AppDispatch>();

  const state = useSelector((root: RootState) => root.restaurantMenu);

  const { show } = useAlerts();
  const { handle } = useApiErrorHandler();
  const confirm = useConfirm();
  const categoryRequestId = useRef(0);
  const itemRequestId = useRef(0);

  // =====================================================
  // 🔹 Load Categories
  // =====================================================

  const loadCategories = async (
    filters: RestaurantMenuCategoryFilters,
    silent = false
  ) => {
    if (!branchId) return;
    const currentRequest = ++categoryRequestId.current;
    dispatch(setMenuError(''));
    dispatch(silent ? setMenuUpdating(true) : setMenuLoading(true));
    try {
      const page = await RestaurantMenuCategoryService.listFiltered(
        branchId,
        filters
      );
      if (currentRequest !== categoryRequestId.current) return;
      dispatch(setMenuCategoryPage(page));
      return page;
    } catch (error: any) {
      if (currentRequest !== categoryRequestId.current) return;
      dispatch(
        setMenuError(
          error?.response?.data?.message ||
            'No fue posible cargar las categorías.'
        )
      );
    } finally {
      if (currentRequest === categoryRequestId.current) {
        dispatch(silent ? setMenuUpdating(false) : setMenuLoading(false));
      }
    }
  };

  // =====================================================
  // 🔹 Load Menu Items
  // =====================================================

  const loadItemOptions = async () => {
    if (!branchId) return;
    try {
      const [categoriesResponse, productsResponse, stationsResponse] =
        await Promise.all([
          RestaurantMenuCategoryService.listFiltered(branchId, {
            page: 0,
            size: 50,
            search: null,
            active: true,
          }),
          RestaurantMenuItemService.products(),
          RestaurantPreparationStationService.list(branchId, {
            page: 0,
            size: 200,
            active: true,
            enabled: true,
          }),
        ]);
      dispatch(setMenuCategories(categoriesResponse.content));
      dispatch(setMenuProducts(productsResponse ?? []));
      dispatch(setMenuStations(stationsResponse.content));
    } catch (error) {
      handle(error);
    }
  };

  const loadItems = async (
    request: RestaurantPageRequest<RestaurantMenuItemFilters>
  ) => {
    if (!branchId) return;
    const currentRequest = ++itemRequestId.current;
    dispatch(setMenuLoading(true));
    try {
      const page = await RestaurantMenuItemService.listFiltered(
        branchId,
        request
      );
      if (currentRequest !== itemRequestId.current) return;
      dispatch(setMenuItemPage(page));
      return page;
    } catch (error) {
      if (currentRequest === itemRequestId.current) handle(error);
    } finally {
      if (currentRequest === itemRequestId.current) {
        dispatch(setMenuLoading(false));
      }
    }
  };

  // =====================================================
  // 🔹 Category Actions
  // =====================================================

  const editCategory = async (category: RestaurantMenuCategoryDTO) => {
    try {
      const response = await RestaurantMenuCategoryService.get(category.id);

      dispatch(openMenuCategoryEdit(response.data));
    } catch (error) {
      handle(error);
    }
  };

  const saveCategory = async (
    filters: RestaurantMenuCategoryFilters,
    onEmptyPage: () => void
  ) => {
    if (!branchId || state.saving) return;

    dispatch(setMenuSaving(true));

    try {
      const response = state.categoryForm.id
        ? await RestaurantMenuCategoryService.update(
            state.categoryForm.id,
            state.categoryForm
          )
        : await RestaurantMenuCategoryService.create(
            branchId,
            state.categoryForm
          );

      show(
        response.type || 'success',
        response.title || 'Categoría guardada',
        response.message || 'La categoría se guardó correctamente.'
      );

      dispatch(closeMenuCategory());

      const page = await loadCategories(filters, true);
      if (page?.content.length === 0 && filters.page > 0) onEmptyPage();
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setMenuSaving(false));
    }
  };

  const toggleCategory = (
    category: RestaurantMenuCategoryDTO,
    filters: RestaurantMenuCategoryFilters,
    onEmptyPage: () => void
  ) => {
    const execute = async () => {
      try {
        const response = await RestaurantMenuCategoryService.updateActive(
          category.id,
          !category.active
        );

        show(
          response.type || 'success',
          response.title || 'Categoría actualizada',
          response.message || 'Estado actualizado.'
        );

        const page = await loadCategories(filters, true);
        if (page?.content.length === 0 && filters.page > 0) onEmptyPage();
      } catch (error) {
        handle(error);
      }
    };

    if (!category.active) {
      execute();
      return;
    }

    confirm({
      title: 'Desactivar categoría',
      message: `¿Desea desactivar “${category.name}”?`,
      icon: 'warning',
      confirmText: 'Sí, desactivar',
      cancelText: 'Cancelar',
      onConfirm: execute,
    });
  };

  // =====================================================
  // 🔹 Menu Item Actions
  // =====================================================

  const editItem = async (item: RestaurantMenuItemDTO) => {
    try {
      const response = await RestaurantMenuItemService.get(item.id);

      dispatch(openMenuItemEdit(response.data));
    } catch (error) {
      handle(error);
    }
  };

  const saveItem = async (
    request: RestaurantPageRequest<RestaurantMenuItemFilters>,
    onEmptyPage: () => void
  ): Promise<RestaurantMenuItemDTO | undefined> => {
    if (!branchId || state.saving) return undefined;

    dispatch(setMenuSaving(true));

    try {
      const response = state.itemForm.id
        ? await RestaurantMenuItemService.update(
            state.itemForm.id,
            state.itemForm
          )
        : await RestaurantMenuItemService.create(branchId, state.itemForm);

      show(
        response.type || 'success',
        response.title || 'Ítem guardado',
        response.message || 'El ítem se guardó correctamente.'
      );

      dispatch(closeMenuItem());

      const page = await loadItems(request);
      if (page?.content.length === 0 && request.page > 0) onEmptyPage();

      return response.data;
    } catch (error) {
      handle(error);
      return undefined;
    } finally {
      dispatch(setMenuSaving(false));
    }
  };

  const toggleAvailable = async (
    item: RestaurantMenuItemDTO,
    request: RestaurantPageRequest<RestaurantMenuItemFilters>,
    onEmptyPage: () => void
  ) => {
    try {
      const response = await RestaurantMenuItemService.updateAvailable(
        item.id,
        !item.available
      );

      show(
        response.type || 'success',
        response.title || 'Disponibilidad actualizada',
        response.message || 'Disponibilidad actualizada.'
      );

      const page = await loadItems(request);
      if (page?.content.length === 0 && request.page > 0) onEmptyPage();
    } catch (error) {
      handle(error);
    }
  };

  const toggleItemActive = (
    item: RestaurantMenuItemDTO,
    request: RestaurantPageRequest<RestaurantMenuItemFilters>,
    onEmptyPage: () => void
  ) => {
    const execute = async () => {
      try {
        const response = await RestaurantMenuItemService.updateActive(
          item.id,
          !item.active
        );

        show(
          response.type || 'success',
          response.title || 'Ítem actualizado',
          response.message || 'Estado actualizado.'
        );

        const page = await loadItems(request);
        if (page?.content.length === 0 && request.page > 0) onEmptyPage();
      } catch (error) {
        handle(error);
      }
    };

    if (!item.active) {
      execute();
      return;
    }

    confirm({
      title: 'Desactivar ítem',
      message: `¿Desea desactivar “${item.displayName || item.productName}”?`,
      icon: 'warning',
      confirmText: 'Sí, desactivar',
      cancelText: 'Cancelar',
      onConfirm: execute,
    });
  };

  // =====================================================
  // 🔹 Form Updates
  // =====================================================

  const updateCategoryField = <K extends keyof RestaurantMenuCategoryForm>(
    field: K,
    value: RestaurantMenuCategoryForm[K]
  ) => {
    dispatch(updateMenuCategoryField({ field, value }));
  };

  const updateItemField = <K extends keyof RestaurantMenuItemForm>(
    field: K,
    value: RestaurantMenuItemForm[K]
  ) => {
    dispatch(updateMenuItemField({ field, value }));
  };

  // =====================================================
  // 🔹 Return
  // =====================================================

  // Alta de estación de preparación al vuelo (nombre; código autogenerado).
  const createStationInline = async (name: string) => {
    if (!branchId) return null;
    try {
      const response = await RestaurantPreparationStationService.create(
        branchId,
        {
          name,
          description: '',
          printerId: '',
          displayOrder: 1,
          estimatedPreparationMinutes: '',
          enabled: true,
        }
      );
      dispatch(setMenuStations([...state.stations, response.data]));
      return response.data;
    } catch (error) {
      handle(error);
      return null;
    }
  };

  // Alta de categoría de menú al vuelo (nombre; el código lo genera el backend).
  const createMenuCategoryInline = async (
    name: string
  ): Promise<RestaurantMenuCategoryDTO | null> => {
    if (!branchId) return null;
    try {
      const response = await RestaurantMenuCategoryService.create(branchId, {
        name,
        description: '',
        icon: '',
        color: '',
        displayOrder: 1,
      });
      dispatch(setMenuCategories([...state.categories, response.data]));
      return response.data;
    } catch (error) {
      handle(error);
      return null;
    }
  };

  return {
    ...state,

    loadCategories,
    resetCategoryResults: () => {
      categoryRequestId.current += 1;
      dispatch(resetMenuCategoryResults());
    },
    loadItems,
    loadItemOptions,
    createMenuCategoryInline,
    createStationInline,

    editCategory,
    saveCategory,
    toggleCategory,

    editItem,
    saveItem,
    toggleAvailable,
    toggleItemActive,

    updateCategoryField,
    updateItemField,

    openCategory: () => dispatch(openMenuCategoryCreate()),
    closeCategory: () => dispatch(closeMenuCategory()),

    openItem: () => dispatch(openMenuItemCreate()),
    closeItem: () => dispatch(closeMenuItem()),
  };
};

export default useRestaurantMenu;
